'use client';

import Checkout from '@/components/Checkout';
import DonorRow from '@/components/DonorRow';
import { useUser } from '@clerk/nextjs';
import { createClient } from '@supabase/supabase-js';
import { X } from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

const ADMIN_ID = 'user_3AtnbF82V4ROWYZ4rH2PlrSsvGk';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL,
  process.env.NEXT_PUBLIC_SUPABASE_KEY
);

export default function ProjectsPage() {
  const { user } = useUser();
  const isAdmin = user?.id === ADMIN_ID;

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [donors, setDonors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target: '',
    stripe_link: ''
  });
  const [viewDonorsProject, setViewDonorsProject] = useState(null);

  // Fetch donors when modal opens
  useEffect(() => {
    if (viewDonorsProject) {
      fetchDonors(viewDonorsProject.id);
    }
  }, [viewDonorsProject]);


  async function fetchDonors(projectId) {
    const { data } = await supabase
      .from('project_donors')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    setDonors(data || []);
  }


  useEffect(() => {
    fetchProjects();

    console.log(projects)
  }, []);

  async function fetchProjects() {
    const { data } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });
    console.log(data)
    setProjects(data || []);
    setLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      name: formData.name,
      description: formData.description,
      target: parseFloat(formData.target),
      stripe_link: formData.stripe_link,
    };

    if (editingProject) {
      await supabase.from('projects').update(payload).eq('id', editingProject.id);
    } else {
      await supabase.from('projects').insert([payload]);
    }

    setFormData({ name: '', description: '', target: '', stripe_link: '' });
    setEditingProject(null);
    fetchProjects();
  }

  const handleDonationSelection = (project, amount) => {
    if (!user?.id) {
     // alert("Sign in to donate.")
     // return;
    }

    const remaining = project.target - (project.total_donated || 0);

    if (remaining <= 0) return; // Project already completed

    if (amount > remaining) {
      alert(`Your donation has been adjusted to $${remaining.toFixed(2)} to avoid exceeding the project goal.`);
      setDonationAmount(remaining);
    } else {
      setDonationAmount(amount);
    }

    setSelectedProject(project.id);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowCreateForm(prev => !prev)
    setFormData({
      name: project.name,
      description: project.description,
      target: project.target,
      stripe_link: project.stripe_link
    });
  };
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedProject, setSelectedProject] = useState()
  const [donationAmount, setDonationAmount] = useState(25);


  const [customProjectId, setCustomProjectId] = useState(null);



  if (loading) return <div className='p-10 bg-amber-50 text-slate-900 min-h-screen'>Loading...</div>;


  return (
    <main className='bg-amber-50 min-h-screen p-5'>
      <div className='text-slate-900 max-w-5xl mx-auto'>
        <h1 className='text-3xl font-bold mb-2'>Projects</h1>
        <p className='italic mb-8 text-slate-600'>
          Donate to projects of your choosing. Projects are created by the General Assembly with goals and purposes.
        </p>

        {isAdmin && !showCreateForm && (
          <button onClick={() => setShowCreateForm(prev => !prev)} className='bg-red-600 mb-5 -mt-5 text-white px-4 py-2 rounded hover:bg-red-700'>
            Create Project
          </button>
        )}

        {isAdmin && showCreateForm && (
          <div className='bg-white p-6 rounded-lg shadow-sm mb-10 border border-amber-200'>
            <div className='flex flex-row justify-between'><h2 className='text-xl font-semibold mb-4'>
              {editingProject ? 'Edit Project' : 'Create New Project'}
            </h2>
              <X onClick={() => setShowCreateForm(prev => !prev)} className='cursor-pointer text-gray-800 hover:text-gray-900'>
                Create Project
              </X></div>
            <form onSubmit={handleSubmit} className='grid gap-4'>
              <input
                className='border p-2 rounded'
                placeholder='Project Name'
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <textarea
                className='border p-2 rounded'
                placeholder='Description'
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <div className='grid grid-cols-2 gap-4'>
                <input
                  className='border p-2 rounded'
                  type='number'
                  placeholder='Target Goal ($)'
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  required
                />
                <input
                  className='border p-2 rounded'
                  placeholder='Stripe Payment Link URL'
                  value={formData.stripe_link}
                  onChange={(e) => setFormData({ ...formData, stripe_link: e.target.value })}
                  required
                />
              </div>
              <div className='flex gap-2'>
                <button type='submit' className='bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700'>
                  {editingProject ? 'Update Project' : 'Create Project'}
                </button>
                {editingProject && (
                  <button
                    type='button'
                    onClick={() => { setEditingProject(null); setFormData({ name: '', description: '', target: '', stripe_link: '' }) }}
                    className='bg-slate-200 px-4 py-2 rounded'
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}



        <div className='grid gap-8'>
          {projects.map((project) => {
            const progress = Math.min(((project.total_donated || 0) / project.target) * 100, 100);
            return (
              <div key={project.id} className='bg-white p-6 rounded-xl shadow-sm border border-amber-100'>
                <div className='flex justify-between items-start mb-2'>
                  <h3 className='text-2xl font-bold text-slate-800'>{project.name}</h3>

                  <div className=' flex flex-row gap-2'>
                    <div className="flex gap-4 mt-0">
                      <button
                        onClick={() => setViewDonorsProject(project)}
                        className="text-blue-500 hover:underline text-sm font-medium"
                      >
                        View Donors {/* ({project.donor_count || 0}) */}
                      </button>
                    </div>
                    <span className='mr-1'>{isAdmin && <span> {project.id}</span>}</span>
                    {isAdmin && (
                      <button
                        onClick={() => handleEdit(project)}
                        className='text-xs bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded'
                      >
                        Edit
                      </button>
                    )}

                  </div>
                </div>

                <p className='text-slate-600 mb-4'>{project.description}</p>

                <div className='flex justify-between text-sm font-medium mb-1'>
                  <span>${project.total_donated?.toLocaleString() || 0} raised</span>
                  <span>Goal: ${project.target?.toLocaleString()}</span>
                </div>

                <div className='w-full bg-slate-100 h-4 rounded-full overflow-hidden mb-4'>
                  <div
                    className='bg-blue-500 h-full transition-all duration-500'
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {(() => {
                  const isCompleted = (project.total_donated || 0) >= project.target;
                  const remaining = Math.max(0, project.target - (project.total_donated || 0));

                  return ( 
                    <div className='mt-6 border-t pt-4 border-amber-100'>
                      {isCompleted ? (
                        <div className='bg-green-50 border border-green-200 rounded-xl p-4 text-center'>
                          <p className='text-green-800 font-bold text-lg'>Goal Achieved</p>
                          <p className='text-green-700 text-sm'>
                            This project has reached its funding target. Thank you to those who donated.
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className='text-xs font-bold text-slate-400 mb-3 uppercase tracking-widest'>
                            Select amount to donate (${remaining.toLocaleString()} remaining)
                          </p>
                          <div className='flex flex-wrap gap-2'>
                            {[5, 10, 20, 50].map((amount) => (
                              <button
                                key={amount}
                                onClick={() => handleDonationSelection(project, amount)}
                                className='flex-1 min-w-[60px] bg-white border-2 border-blue-500 text-blue-600 py-2 rounded-xl font-bold hover:bg-blue-500 hover:text-white transition-all active:scale-95'
                              >
                                ${amount}
                              </button>
                            ))}

                            {customProjectId === project.id ? (
                              <div className='flex items-center gap-2 w-full mt-2'>
                                <div className='relative flex-1'>
                                  <span className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400'>$</span>
                                  <input
                                    type='number'
                                    className='w-full border-2 border-blue-500 rounded-xl py-2 pl-7 pr-3 outline-none'
                                    placeholder='Other'
                                    onChange={(e) => setDonationAmount(Number(e.target.value))}
                                  />
                                </div>
                                <button
                                  onClick={() => handleDonationSelection(project, donationAmount)}
                                  className='bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold'
                                >
                                  Donate
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setCustomProjectId(project.id)}
                                className='flex-1 min-w-[80px] bg-slate-100 text-slate-600 py-2 rounded-xl font-bold hover:bg-slate-200'
                              >
                                Custom
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* View Donors Modal */}
                {viewDonorsProject && (
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
                      <div className="p-6 border-b flex justify-between items-center">
                        <h3 className="text-xl font-bold">Donors for {viewDonorsProject.name}</h3>
                        <button
                          onClick={() => setViewDonorsProject(null)}
                          className="text-slate-400 hover:text-black text-2xl"
                        >
                          &times;
                        </button>
                      </div>
                      <div className="p-6 max-h-[60vh] overflow-y-auto">
                        {donors.length > 0 ? (
                          donors.map((donor) => <DonorRow key={donor.id} donor={donor} />)
                        ) : (
                          <p className="text-center text-slate-500 py-10">No donations yet. Be the first!</p>
                        )}
                      </div>
                    </div> 
                  </div>
                )}

                {/* Modal for Stripe Embedded Checkout */}
                {selectedProject === project.id && (
                  <div className='fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex overflow-contain items-center justify-center p-4'>
                    <div className='bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden shadow-2xl relative'>
                      <div className='p-6 border-b flex justify-between items-center bg-amber-50 sticky top-0 z-10'>
                        <div>
                          <h2 className='text-xl font-bold text-slate-800'>{project.name}</h2>
                          <p className='text-blue-600 font-bold'>Total Contribution: ${donationAmount}</p>
                        </div>
                        <button
                          onClick={() => setSelectedProject(null)}
                          className='w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-red-500 hover:text-white transition-colors'
                        >
                          <span className='text-xl'>&times;</span>
                        </button>
                      </div>

                      <div className='p-4 overflow-y-auto max-h-[calc(90vh-100px)]'>
                        <Checkout projectId={project.id} amount={donationAmount} userId={user?.id} />
                      </div>
                    </div>
                  </div>
                )}

              </div>


            );
          })}
        </div>
      </div>
    </main>
  );
}